import { OrderDirection, OrderType } from 'store/spot/types'
import {
  AccountId,
  Ether,
  OrderId,
  SymbolId,
  SymbolName,
  Timestamp,
  UserId,
} from 'types'
import { ApiV1Response, http } from './http'
import { CreateOrderBody, SpotOrderStatus, TradingFeeRates } from './spot'

export interface PerpOpenOrdersData {
  results: PerpOrderRecord[]
}
export interface PerpOrderRecord {
  id: OrderId // 2552832305
  userId: UserId // 10080
  accountId: AccountId // 0
  clientOrderId: string // '@2552832305'
  symbolId: number // 100103
  sequenceId: number // 255283
  type: OrderType // "LIMIT"
  status: SpotOrderStatus // "PENDING"
  direction: OrderDirection // "LONG"
  features: number // 0
  price: number // 40000.0
  fee: number // 0.0
  fillPrice: number // 0.0
  makerFeeRate: number // 0.001
  takerFeeRate: number // 0.002
  createdAt: Timestamp // 1684735960126
  updatedAt: Timestamp // 1684735960126
  marginCurrencyId: number // 103
  unfilledQuantity: number // 1.00
  frozenMargin: number // 800.000000000000000000
  quantity: number // "2"
  symbol: SymbolName // "XBTC"
  triggerDirection: OrderDirection // "LONG"
  triggerOn: Ether // "0.0"
}

export interface PerpPositionsData {
  results: PerpPositionRecord[]
}
export interface PerpClosedOrdersData {
  range: string // "202305", date
  hasMore: boolean // false, has more record
  nextOffsetId: number // 0, offsetId(first of next page) for requesting the next page of data
  results: PerpOrderRecord[]
}
export interface PerpPositionClearingsData {
  range: string // "202305", date
  hasMore: boolean // false, has more record
  nextOffsetId: number // 0, offsetId(first of next page) for requesting the next page of data
  results: PerpPositionClearingsRecord[]
}

export enum PositionClearingType {
  OPEN = 'OPEN',
  CLOSE = 'CLOSE',
  FUNDING = 'FUNDING',
  INCREASE = 'INCREASE',
  DECREASE = 'DECREASE',
  DELEVERAGE_DECREASE = 'DELEVERAGE_DECREASE', // position deleveraged and decreased
  DELEVERAGE_CLOSE = 'DELEVERAGE_CLOSE', // position deleveraged and closed
  MERGE = 'MERGE', // position merged from liquidated
  LIQUIDATE = 'LIQUIDATE', // position liquidated
}

export interface PerpPositionClearingsRecord {
  id?: number
  orderId: number
  sequenceId: number
  direction: OrderDirection
  type: PositionClearingType
  clearingPrice: number
  rate: number
  fee: number
  quantityChanged: number
  quantityAfterClearing: number
  realizedPNLChanged: number
  createdAt: Timestamp
  symbol: SymbolName
  txHash: string
}
export interface PerpPositionRecord {
  direction: OrderDirection
  userId: UserId // 10080
  accountId: AccountId // 0
  symbolId: SymbolId // 145
  id: string // '10081_0_145'
  riskLevel: number // 0
  riskLimitId: number // 106
  riskLimit: {
    id: number // 106
    initialMarginRate: number // 0.02
    maintenanceMarginRate: number // 0.01
    marginRateStep: number // 0.01
    maxLeverage: number // 5
    riskLimitBase: number // 200
    riskLimitStep: number // 100
    maxRiskLimitSteps: number // 9
    createdAt: number // 1546956031080
  }
  quantity: number // 0
  maxQuantity: number // 200
  updatedAt: number // 1684916586063
  realizedPNL: number // 0
  entryPrice: number // 0
  entryValue: number // 0
  minimumMaintenanceMarginRate: number // 0.01
  requiredMargin: number // 0
  minimumMaintenanceMargin: number // 0
  unrealizedPNL: number // 0
  symbol: SymbolName
  closed: boolean // true
  leverage: number
}
export interface PerpClosedOrdersParams {
  range?: string
  symbol?: SymbolName
  offsetId?: number
  limit?: number
}
export interface ClosePerpOrdersBody {
  symbol: string
  slotId: number
  signature: string
  nonce: number
  price: number
  clientOrderId: string
}

export async function getPerpOpenOrders<
  T extends ApiV1Response<PerpOpenOrdersData>,
>(accountId: number): Promise<T> {
  return http
    .get<T>(`/trading/${accountId}/contracts/orders/open`)
    .then((r) => r.data)
}

// Get all position data of the user
export async function getPerpPositions<
  T extends ApiV1Response<PerpPositionsData>,
>(accountId: number): Promise<T> {
  return http
    .get<T>(`/trading/${accountId}/contracts/positions`)
    .then((r) => r.data)
}

export interface CreatePerpOrderResponse {
  orderId: number
}

export async function postCreatePerpOrder<
  T extends ApiV1Response<CreatePerpOrderResponse>,
>(accountId: number, data: CreateOrderBody): Promise<T> {
  return http
    .post<T>(`/trading/${accountId}/contracts/orders`, data)
    .then((r) => r.data)
}
export async function getPerpFeeRates<
  T extends ApiV1Response<TradingFeeRates>,
>(): Promise<T> {
  return http.get<T>(`/trading/contracts/fee/rate`).then((r) => r.data)
}
export async function getPerpClosedOrders<
  T extends ApiV1Response<PerpClosedOrdersData>,
>(accountId: number, params: PerpClosedOrdersParams): Promise<T> {
  return http
    .get<T>(`/trading/${accountId}/contracts/orders/closed`, {
      params: {
        limit: 10,
        symbol: '',
        offsetId: 0,
        ...params,
      },
    })
    .then((r) => r.data)
}

export async function getPerpPositionClearings<
  T extends ApiV1Response<PerpPositionClearingsData>,
>(accountId: number, params: PerpClosedOrdersParams): Promise<T> {
  return http
    .get<T>(`/trading/${accountId}/contracts/clearings/positions`, {
      params: {
        limit: 10,
        range: '',
        symbol: '',
        offsetId: 0,
        ...params,
      },
    })
    .then((r) => r.data)
}

export async function cancelPerpOrdersById<
  T extends ApiV1Response<PerpClosedOrdersData>,
>(accountId: number, orderId: number): Promise<T> {
  return http
    .post<T>(`/trading/${accountId}/contracts/orders/${orderId}/cancel`)
    .then((r) => r.data)
}

export interface CancelPerpOrdersBody {
  symbol?: string
  type?: 'LIMIT' | 'STOP'
}
export async function cancelPerpAllOrders<
  T extends ApiV1Response<PerpClosedOrdersData>,
>(accountId: number, data: CancelPerpOrdersBody): Promise<T> {
  return http
    .post<T>(`/trading/${accountId}/contracts/orders/cancel`, data)
    .then((r) => r.data)
}

export async function closePerpOrdersBySymbol<
  T extends ApiV1Response<PerpClosedOrdersData>,
>(accountId: number, data: ClosePerpOrdersBody): Promise<T> {
  return http
    .post<T>(`/trading/${accountId}/contracts/positions/close`, data)
    .then((r) => r.data)
}

export type BatchClosePositionResponse = Record<SymbolName, OrderId>
export async function batchClosePosition<
  T extends ApiV1Response<BatchClosePositionResponse>,
>(accountId: number, data: ClosePerpOrdersBody[]): Promise<T> {
  return http
    .post<T>(`/trading/${accountId}/contracts/positions/batch/close`, {
      closeOrderList: data,
    })
    .then((r) => r.data)
}
